import { HTMLAttributes, memo, useCallback, useEffect, useState } from 'react';
import { TextField } from 'react-aria-components';

import { merge } from '@/components';
import { Heading, Label, Text, TextArea } from '@/components/core';
import { useDebounceCallback, useFormatter, useMappingFormatterMapping } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { FieldContextType, FieldStateValueType } from '@/utils';

import { findField } from './utils';

export type FormatterTestProps = {
  fieldId: string;
  parentId?: string | null;
  formatterId: string;
} & HTMLAttributes<HTMLDivElement>;

const IFormatterTest = ({ fieldId, parentId, formatterId, className, ...rest }: FormatterTestProps) => {
  const [rawValue, setRawValue] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [value, setValue] = useState<string | undefined>(undefined);
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);
  const { mapping } = useMappingFormatterMapping({ fieldId, parentId, formatterId });

  const { run } = useFormatter({ fieldId, formatterId });

  const onInputChanged = useDebounceCallback(
    useCallback(async (value: string | undefined) => {
      setErrorMessage(undefined);
      setRawValue(value);

      if (!value) {
        setValue(undefined);
      }
    }, []),
    500
  );

  useEffect(() => {
    const wrapper = async () => {
      if (!formatter || !rawValue) return;

      const fieldValue: FieldStateValueType = { rawValue, confidence: 1.0 };

      const context: FieldContextType = {
        name: formatter.name,
        fields: { fieldId: fieldValue },
        config: formatter.config,
      };

      if (mapping) context.config.mapping = mapping;

      try {
        const formatted = await run(fieldValue, context);
        if (formatted.value != null) {
          setValue(formatted.value);
        } else {
          setErrorMessage('This input produces an empty result.');
        }
      } catch (error) {
        if (error instanceof Error) {
          setValue(undefined);
          setErrorMessage(error.message);
        }
      }
    };

    wrapper();
  }, [formatter, mapping, rawValue, run]);

  return (
    <div className={merge('gap-sm flex flex-col', className)} {...rest}>
      <Heading size="sm">Test formatter</Heading>

      <TextField onChange={onInputChanged} className="flex w-full flex-col">
        <TextArea placeholder="Enter a value to preview the formatted result" />
      </TextField>

      {rawValue ? (
        <TextField className="flex w-full flex-col">
          {value ? (
            <>
              <Label>Result</Label>
              <TextArea value={value} disabled />
            </>
          ) : (
            <Text slot="description" variant="warning">
              {errorMessage}
            </Text>
          )}
        </TextField>
      ) : null}
    </div>
  );
};

export const FormatterTest = memo(IFormatterTest) as typeof IFormatterTest;
